import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Clock } from 'lucide-react';
import { getFileUrl } from '../config/api';

interface PendingReview {
  jobId: string;
  visitId: string;
  date: string;
  actualStartTime?: string;
  actualEndTime: string;
  serviceType: string;
  otherParty: {
    _id: string;
    firstName: string;
    lastName: string;
    profilePhoto?: string;
  };
}

const PendingReviews: React.FC = () => {
  const [reviews, setReviews] = useState<PendingReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState<PendingReview | null>(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      const res = await axios.get('/api/reviews/pending');
      if (res.data.success) {
        setReviews(res.data.pendingReviews);
      }
    } catch (error) {
      console.error('Error fetching pending reviews', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedReview) return;
    setSubmitting(true);
    try {
      await axios.post('/api/reviews', {
        jobId: selectedReview.jobId,
        visitId: selectedReview.visitId,
        rating,
        reviewText: comment
      });
      // Success
      setShowModal(false);
      setReviews(prev => prev.filter(r => r.visitId !== selectedReview.visitId));
      setComment('');
      setRating(5);
    } catch (error) {
      console.error('Submit review error', error);
      alert('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || reviews.length === 0) return null;

  return (
    <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl shadow-sm">
      <h3 className="text-lg font-bold text-yellow-800 mb-3 flex items-center gap-2">
        <Star className="w-5 h-5 fill-yellow-500 text-yellow-500" />
        Pending Reviews ({reviews.length})
      </h3>
      <div className="flex flex-col gap-4">
        {reviews.map(review => (
          <div key={review.visitId} className="bg-white p-4 rounded-xl border border-yellow-100 shadow-sm flex flex-col gap-3 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <img 
                src={getFileUrl(review.otherParty?.profilePhoto || '')} 
                alt="Profile" 
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40'; }}
              />
              <div>
                <p className="font-semibold text-gray-900">
                    {review.otherParty?.firstName} {review.otherParty?.lastName}
                </p>
                <p className="text-xs text-gray-500 capitalize">{review.serviceType}</p>
              </div>
            </div>
            <div className="text-sm text-gray-600 flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>{new Date(review.date).toLocaleDateString()}</span>
                </div>
                {review.actualStartTime && review.actualEndTime && (
                  <div className="text-xs text-gray-500 ml-4">
                    {new Date(review.actualStartTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                    {new Date(review.actualEndTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                )}
            </div>
            <button 
              onClick={() => {
                setSelectedReview(review);
                setShowModal(true);
              }}
              className="mt-auto w-full py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Write Review
            </button>
          </div>
        ))}
      </div>

      {showModal && selectedReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">Rate your shift with {selectedReview.otherParty?.firstName}</h3>
            
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <Star 
                    className={`w-8 h-8 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                  />
                </button>
              ))}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="How was your experience? (Optional)"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 min-h-[100px] mb-6 resize-none"
            />

            <div className="flex gap-3">
                <button 
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 text-gray-600 font-semibold hover:bg-gray-50 rounded-lg transition-colors"
                >
                    Cancel
                </button>
                <button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-2.5 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                >
                    {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingReviews;
